import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import tokens from '@verifime/design-tokens';
import { useState } from 'react';
import { formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import { ColoredIcon } from '../../ColoredIconAndChip';
import { FilePreviewModal } from '../../FileUploader/FilePreviewModal';
import { enqueNotificationWithOptions } from '../../Notification';
import PortraitDisplay, { TPortraitDisplayProps } from '../PortraitDisplay';

export type TlivenessViewerProps = {
  checkedDateTime?: string;
  generateVideoDownloadUrl?: () => Promise<string | null>;
  videoDownloadErrorMessage?: string;
} & Pick<TPortraitDisplayProps, 'portraitImageUrl' | 'action'>;

function CheckedDateTimeDisplay({
  checkedDateTime,
}: Readonly<Pick<TlivenessViewerProps, 'checkedDateTime'>>) {
  return (
    <>
      {checkedDateTime && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" color="textSecondary">
            Completed on
          </Typography>
          <Typography variant="body2">{formatToDDMMMYYYYHHMMAAA(checkedDateTime)}</Typography>
        </Stack>
      )}
    </>
  );
}

export default function LivenessViewer({
  generateVideoDownloadUrl,
  videoDownloadErrorMessage = 'Failed to generate video download url',
  ...props
}: Readonly<TlivenessViewerProps>) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  const videoClickHandler = async () => {
    try {
      if (generateVideoDownloadUrl) {
        const url = await generateVideoDownloadUrl();
        if (url) {
          setVideoUrl(url);
        } else {
          enqueNotificationWithOptions('errorNotification', videoDownloadErrorMessage, {
            autoHideDuration: 20000,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load video:', error);
      enqueNotificationWithOptions('errorNotification', videoDownloadErrorMessage, {
        autoHideDuration: 20000,
      });
    }
  };

  return (
    <>
      <PortraitDisplay
        {...props}
        footer={
          <Stack gap={tokens.spacingXs} py={tokens.spacingXs} alignItems="center">
            <CheckedDateTimeDisplay {...props} />
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                videoClickHandler();
              }}
            >
              <Stack
                direction="row"
                gap={tokens.spacing2xs}
                justifyItems="center"
                alignItems="center"
              >
                Play Video
                <ColoredIcon iconName="openInNewRounded" />
              </Stack>
            </Link>
          </Stack>
        }
      />
      <FilePreviewModal
        open={videoUrl !== null}
        onClose={() => setVideoUrl(null)}
        fileUrl={videoUrl ?? undefined}
        mimeType="video/mp4"
      />
    </>
  );
}
